import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { getCustomerDisplayName, getCustomerOptionLabel } from "../utils/customerDisplay";
import { CreateIncidentModal } from "../components/incidents/CreateIncidentModal";
import { IncidentDetailModal } from "../components/incidents/IncidentDetailModal";
import { ResolveIncidentModal } from "../components/incidents/ResolveIncidentModal";
import { IncidentStats } from "../components/incidents/IncidentStats";
import { IncidentsTable } from "../components/incidents/IncidentsTable";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { AlertTriangle, Plus, Download } from "lucide-react";
import { downloadCSV, csvDate } from "../utils/csv";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusLabels = {
  NUEVA: "Nueva",
  EN_TRATAMIENTO: "En Tratamiento",
  PENDIENTE_PROVEEDOR: "Pendiente Proveedor",
  PENDIENTE_CLIENTE: "Pendiente Cliente",
  RESUELTO: "Resuelto",
};

const statusColors = {
  NUEVA: "bg-red-500",
  EN_TRATAMIENTO: "bg-blue-500",
  PENDIENTE_PROVEEDOR: "bg-purple-500",
  PENDIENTE_CLIENTE: "bg-yellow-500",
  RESUELTO: "bg-green-500",
};

const priorityLabels = {
  BAJA: "Baja",
  MEDIA: "Media",
  ALTA: "Alta",
  CRITICA: "Crítica",
};

const priorityColors = {
  BAJA: "bg-slate-500",
  MEDIA: "bg-blue-500",
  ALTA: "bg-orange-500",
  CRITICA: "bg-red-600",
};

const IncidentsPage = () => {
  const { user, hasPermission, canDelete, canExport } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [resolutionSummary, setResolutionSummary] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [incidentToDelete, setIncidentToDelete] = useState(null);
  
  const [newIncident, setNewIncident] = useState({
    customer_id: "",
    service_id: "",
    location_id: "",
    title: "",
    description: "",
    priority: "MEDIA",
    assigned_to_id: "",
  });

  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [supportUsers, setSupportUsers] = useState([]); // technicians available for assignment

  // Filter customers by search term (nombre, DNI, código)
  const filteredCustomers = customers.filter(c => {
    if (!customerSearch) return true;
    const search = customerSearch.toLowerCase();
    // Use the canonical display name so empresa rows match by `razon_social`
    // (the `nombre_fiscal` field used previously did not exist on the model).
    const display = getCustomerDisplayName(c).toLowerCase();
    const apellidos = (c.apellidos || "").toLowerCase();
    const dni = (c.dni_nie_cif || c.dni_nie || c.cif || "").toLowerCase();
    const codigo = (c.customer_id || "").toLowerCase();
    return display.includes(search) || apellidos.includes(search) ||
           dni.includes(search) || codigo.includes(search);
  });

  const fetchIncidents = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (priorityFilter !== "all") params.priority = priorityFilter;
      
      const response = await axios.get(`${API_URL}/api/incidents`, { params });
      setIncidents(response.data);
    } catch (error) {
      console.error("Error fetching incidents:", error);
      toast.error("Error al cargar incidencias");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/incidents/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchSupportUsers = async () => {
    // Same strategy as Tickets Internos: prefer users with SOPORTE/TECNICO role,
    // fall back to all active users so the assign dropdown is never empty.
    try {
      const [tecRes, supRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/by-role/TECNICO`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/users/by-role/SOPORTE`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/users`).catch(() => ({ data: [] })),
      ]);
      const byId = new Map();
      [...(tecRes.data || []), ...(supRes.data || [])].forEach(u => { if (u?.id) byId.set(u.id, u); });
      let list = Array.from(byId.values());
      if (list.length === 0) {
        list = (allRes.data || []).filter(u => u.is_active !== false);
      }
      setSupportUsers(list);
    } catch (e) {
      console.error("Error fetching support users:", e);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchCustomerServices = async (customerId) => {
    try {
      const response = await axios.get(`${API_URL}/api/customer-services`, {
        params: { customer_id: customerId }
      });
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  useEffect(() => {
    fetchIncidents();
    fetchStats();
    fetchCustomers();
    fetchSupportUsers();
  }, [fetchIncidents]);

  // Handle URL params for auto-opening create modal
  useEffect(() => {
    const action = searchParams.get("action");
    const customerId = searchParams.get("customer");
    const serviceId = searchParams.get("service");

    if (action === "create") {
      if (customerId) {
        // Find customer to get display name
        const customer = customers.find(c => c.id === customerId);
        if (customer) {
          setCustomerSearch(getCustomerOptionLabel(customer));
          setNewIncident(prev => ({ ...prev, customer_id: customerId, service_id: serviceId || "" }));
        } else {
          setNewIncident(prev => ({ ...prev, customer_id: customerId, service_id: serviceId || "" }));
        }
      }
      setCreateModalOpen(true);
    }
  }, [searchParams, customers]);

  // Deep-link: open specific incident detail when ?open=<id> is present
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || incidents.length === 0 || detailModalOpen) return;
    const target = incidents.find((i) => i.id === openId);
    if (target) {
      openDetailModal(target);
      // Clear the query param so refresh does not reopen
      navigate("/incidents", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, incidents]);

  useEffect(() => {
    if (newIncident.customer_id) {
      fetchCustomerServices(newIncident.customer_id);
    }
  }, [newIncident.customer_id]);

  const handleCreateIncident = async () => {
    if (!newIncident.customer_id || !newIncident.title || !newIncident.description) {
      toast.error("Cliente, título y descripción son obligatorios");
      return;
    }

    try {
      // Normalize "Sin asignar" (empty string) → null so the backend stores
      // a clean unassigned value instead of an invalid user id.
      const payload = {
        ...newIncident,
        assigned_to_id: newIncident.assigned_to_id || null,
      };
      await axios.post(`${API_URL}/api/incidents`, payload);
      toast.success("Incidencia creada correctamente");
      setCreateModalOpen(false);
      setNewIncident({
        customer_id: "",
        service_id: "",
        location_id: "",
        title: "",
        description: "",
        priority: "MEDIA",
        assigned_to_id: "",
      });
      fetchIncidents();
      fetchStats();
    } catch (error) {
      console.error("Error creating incident:", error);
      toast.error(error.response?.data?.detail || "Error al crear incidencia");
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedIncident) return;
    
    // If resolving, open resolution modal
    if (newStatus === "RESUELTO") {
      setResolveModalOpen(true);
      return;
    }

    try {
      const res = await axios.put(`${API_URL}/api/incidents/${selectedIncident.id}`, {
        status: newStatus
      });
      const updatedIncident = res.data;
      toast.success("Estado actualizado");
      
      // Update local state immediately for real-time UI
      setSelectedIncident(updatedIncident);
      setIncidents(prevIncidents => 
        prevIncidents.map(i => i.id === selectedIncident.id ? { ...i, ...updatedIncident } : i)
      );
      
      // Refresh stats
      fetchStats();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar estado");
    }
  };

  const handleAssignTechnician = async (assigneeId) => {
    if (!selectedIncident) return;
    try {
      const res = await axios.put(`${API_URL}/api/incidents/${selectedIncident.id}`, {
        assigned_to_id: assigneeId || null,
      });
      const updated = res.data;
      toast.success(assigneeId ? "Técnico asignado" : "Asignación eliminada");
      setSelectedIncident(updated);
      setIncidents(prev => prev.map(i => i.id === selectedIncident.id ? { ...i, ...updated } : i));
    } catch (error) {
      console.error("Error assigning technician:", error);
      toast.error(error.response?.data?.detail || "Error al asignar técnico");
    }
  };

  const handleResolveIncident = async () => {
    if (!resolutionSummary.trim()) {
      toast.error("El resumen de la solución es obligatorio");
      return;
    }

    try {
      const res = await axios.put(`${API_URL}/api/incidents/${selectedIncident.id}`, {
        status: "RESUELTO",
        resolution_summary: resolutionSummary
      });
      const updatedIncident = res.data;
      toast.success("Incidencia resuelta");
      setResolveModalOpen(false);
      setResolutionSummary("");
      
      // Update local state immediately for real-time UI
      setSelectedIncident(updatedIncident);
      setIncidents(prevIncidents => 
        prevIncidents.map(i => i.id === selectedIncident.id ? { ...i, ...updatedIncident } : i)
      );
      fetchStats();
    } catch (error) {
      console.error("Error resolving incident:", error);
      toast.error(error.response?.data?.detail || "Error al resolver incidencia");
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedIncident) return;

    try {
      const res = await axios.post(`${API_URL}/api/incidents/${selectedIncident.id}/comments`, {
        content: newComment
      });
      const updatedIncident = res.data;
      toast.success("Comentario añadido");
      setNewComment("");
      
      // Update local state immediately for real-time UI
      setSelectedIncident(updatedIncident);
      setIncidents(prevIncidents => 
        prevIncidents.map(i => i.id === selectedIncident.id ? { ...i, ...updatedIncident } : i)
      );
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Error al añadir comentario");
    }
  };

  const handleDeleteIncident = async () => {
    if (!incidentToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/api/incidents/${incidentToDelete.id}`);
      toast.success("Incidencia eliminada correctamente");
      setDeleteDialogOpen(false);
      setIncidentToDelete(null);
      setDetailModalOpen(false);
      fetchIncidents();
      fetchStats();
    } catch (error) {
      console.error("Error deleting incident:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar incidencia");
    }
  };

  const openDeleteDialog = (e, incident) => {
    e.stopPropagation();
    setIncidentToDelete(incident);
    setDeleteDialogOpen(true);
  };

  const openDetailModal = async (incident) => {
    try {
      const response = await axios.get(`${API_URL}/api/incidents/${incident.id}`);
      setSelectedIncident(response.data);
      setDetailModalOpen(true);
    } catch (error) {
      console.error("Error fetching incident details:", error);
      toast.error("Error al cargar detalles");
    }
  };

  const filteredIncidents = incidents.filter(inc => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        inc.incident_number?.toLowerCase().includes(search) ||
        inc.title?.toLowerCase().includes(search) ||
        inc.customer_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const canEdit = user?.roles?.some(r => ["TECNICO", "GESTION", "SUPERADMIN"].includes(r));

  const handleExportCSV = () => {
    if (filteredIncidents.length === 0) {
      toast.error("No hay incidencias para exportar");
      return;
    }
    const columns = [
      { key: "incident_number", label: "Nº Incidencia" },
      { key: "title", label: "Título" },
      { key: "customer_name", label: "Cliente" },
      { key: "status", label: "Estado", format: (v) => statusLabels[v] || v || "" },
      { key: "priority", label: "Prioridad", format: (v) => priorityLabels[v] || v || "" },
      { key: "assigned_to_name", label: "Asignado a" },
      { key: "created_at", label: "Creada", format: csvDate },
      { key: "resolved_at", label: "Resuelta", format: csvDate },
    ];
    downloadCSV("incidencias", columns, filteredIncidents);
    toast.success(`${filteredIncidents.length} incidencias exportadas a CSV`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="incidents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-[#03BEE4]" />
            Incidencias
          </h1>
          <p className="text-slate-400 mt-1">
            Gestión de incidencias técnicas
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canExport("incidents") && (
            <Button
              onClick={handleExportCSV}
              variant="outline"
              className="border-[#42E695] text-[#42E695] hover:bg-[#42E695]/20"
              data-testid="export-incidents-csv-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar CSV
            </Button>
          )}
          {hasPermission("incidents", "create") && (
            <Button
              onClick={() => setCreateModalOpen(true)}
              className="bg-[#42E695] text-[#04042C] hover:bg-[#34D399]"
              data-testid="create-incident-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nueva Incidencia
            </Button>
          )}
        </div>
      </div>

      <IncidentStats
        stats={stats}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
      />

      {/* Incidents Table */}
      <IncidentsTable
        filteredIncidents={filteredIncidents}
        statusColors={statusColors}
        statusLabels={statusLabels}
        priorityColors={priorityColors}
        priorityLabels={priorityLabels}
        formatDate={formatDate}
        openDetailModal={openDetailModal}
        canDelete={canDelete}
        openDeleteDialog={openDeleteDialog}
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        incidentToDelete={incidentToDelete}
        handleDeleteIncident={handleDeleteIncident}
      />

      {/* Create Incident Modal */}
      <CreateIncidentModal
        createModalOpen={createModalOpen}
        setCreateModalOpen={setCreateModalOpen}
        customerSearch={customerSearch}
        setCustomerSearch={setCustomerSearch}
        showCustomerDropdown={showCustomerDropdown}
        setShowCustomerDropdown={setShowCustomerDropdown}
        newIncident={newIncident}
        setNewIncident={setNewIncident}
        services={services}
        setServices={setServices}
        filteredCustomers={filteredCustomers}
        supportUsers={supportUsers}
        handleCreateIncident={handleCreateIncident}
      />

      {/* Detail Modal */}
      <IncidentDetailModal
        detailModalOpen={detailModalOpen}
        setDetailModalOpen={setDetailModalOpen}
        selectedIncident={selectedIncident}
        statusColors={statusColors}
        statusLabels={statusLabels}
        priorityColors={priorityColors}
        priorityLabels={priorityLabels}
        canEdit={canEdit}
        handleStatusChange={handleStatusChange}
        handleAssignTechnician={handleAssignTechnician}
        supportUsers={supportUsers}
        formatDate={formatDate}
        newComment={newComment}
        setNewComment={setNewComment}
        handleAddComment={handleAddComment}
      />

      {/* Resolution Modal */}
      <ResolveIncidentModal
        resolveModalOpen={resolveModalOpen}
        setResolveModalOpen={setResolveModalOpen}
        resolutionSummary={resolutionSummary}
        setResolutionSummary={setResolutionSummary}
        handleResolveIncident={handleResolveIncident}
      />
    </div>
  );
};

export default IncidentsPage;
